import { createFetch } from '@vueuse/core'
import * as Sentry from '@sentry/vue'
import { aiCoordinatorEndpoints, endpoints } from '@/utils/endpoints'
export interface ApiError {
  detail: string
  source: {
    pointer: string
  }
  status: number
  title?: string
  code?: string
}

export interface ApiResponseError {
  errors: ApiError[]
}

export const requestFailed = (
  statusCode: { value: number | null },
  error: { value: unknown },
) => !!error.value || !!(statusCode.value && statusCode.value >= 400)

export const useApi = createFetch({
  baseUrl: `${import.meta.env.VITE_SH_URL}/api/spots/`,
  options: {
    async beforeFetch(ctx) {
      // const authStore = useAuthStore()
      ctx.options.headers = {
        ...ctx.options.headers,
        //Accept: 'application/json',
        //'Content-Type': 'application/json',
        // 'Content-Type': 'application/x-www-form-urlencoded'
        // Authorization: `Bearer ${authStore.token?.id ?? ''}`,
      }

      return ctx
    },
    async onFetchError(ctx) {
      const appStore = useAppStore()

      if (ctx.response && ctx.response.status >= 408) {
        appStore.setIOSError({
          statusCode: ctx.response.status ?? null,
          action: 'showMessage',
          message: 'Please try again later.',
          url: window.location.href,
        })

        Sentry.captureMessage('Web App Show Message Error', {
          level: 'warning',
          extra: {
            statusCode: ctx.response.status,
            message: 'Please try again later.',
            url: window.location.href,
          },
        })

        appStore.sendErrorToiOS()
      }

      return ctx
    },
  },
  fetchOptions: {
    mode: 'cors',
    credentials: 'include',
    cache: 'no-cache',
  },
})

let coordinatorTokenRequest: Promise<string | undefined> | null = null

async function fetchCoordinatorToken() {
  const authStore = useAuthStore()
  const { data } = await useApi(
    endpoints().aiCoordinatorToken(authStore.spotId),
  ).json()

  const idToken = data.value?.id_token as string | undefined
  if (idToken) {
    authStore.setToken({ id: idToken, refresh: data.value?.refresh_token ?? '' })
  }
  return idToken
}

async function refreshCoordinatorToken() {
  const authStore = useAuthStore()
  const refreshToken = authStore.token?.refresh
  if (!refreshToken) return undefined

  const response = await fetch(
    `${import.meta.env.VITE_COORDINATOR_URL}/${aiCoordinatorEndpoints().refreshAiCoordinatorToken()}`,
    {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    },
  ).catch(() => null)

  const idToken = response?.ok
    ? ((await response.json().catch(() => null))?.idToken as string | undefined)
    : undefined
  if (idToken) authStore.setToken({ id: idToken, refresh: refreshToken })
  return idToken
}

function renewCoordinatorToken() {
  coordinatorTokenRequest ??= (async () => {
    try {
      return (await refreshCoordinatorToken()) ?? (await fetchCoordinatorToken())
    } finally {
      coordinatorTokenRequest = null
    }
  })()

  return coordinatorTokenRequest
}

async function coordinatorFetch(input: RequestInfo | URL, init?: RequestInit) {
  const response = await fetch(input, init)
  if (response.status !== 401) return response

  const newToken = await renewCoordinatorToken()
  if (!newToken) return response

  return fetch(input, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${newToken}`,
    },
  })
}

export const useCoordinatorApi = createFetch({
  baseUrl: `${import.meta.env.VITE_COORDINATOR_URL}/`,
  options: {
    fetch: coordinatorFetch,
    async beforeFetch(ctx) {
      const authStore = useAuthStore()
      if (!authStore.token?.id) await renewCoordinatorToken()

      ctx.options.headers = {
        ...ctx.options.headers,
        Authorization: `Bearer ${authStore.token?.id ?? ''}`,
      }

      return ctx
    },
    async onFetchError(ctx) {
      const appStore = useAppStore()

      if (ctx.response && ctx.response.status >= 408) {
        appStore.setIOSError({
          statusCode: ctx.response.status ?? null,
          action: 'showMessage',
          message: 'Please try again later.',
          url: window.location.href,
        })

        Sentry.captureMessage('Web App Show Message Error', {
          level: 'warning',
          extra: {
            statusCode: ctx.response.status,
            message: 'Please try again later.',
            url: window.location.href,
          },
        })

        appStore.sendErrorToiOS()
      }

      return ctx
    },
  },
  fetchOptions: {
    mode: 'cors',
    credentials: 'include',
    cache: 'no-cache',
  },
})
